<script>
export default {
	name: 'EditUpdatePopup',
	inheritAttrs: false,
};
</script>

<script setup>
import CTAButton from '~/aem-components/CTAButton.vue';
import TextField from '~/components/form/TextField.vue';
import MHTextArea from '~/components/form/MHTextArea.vue';
import OverlayBtmSticky from '~/components/OverlayBtmSticky.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';

const props = defineProps({
	isVisible: { type: Boolean, default: false },
	editCancelCtaLabel: { type: String, default: 'Cancel' },
	editUpdateCtaLabel: { type: String, default: 'Yes' },
	editListTitleText: { type: String, default: 'Remove list' },
	duplicationMessage: { type: String, default: '' },
	fallbackErrorMessage: { type: String, default: '' },
	listNamePlaceholderText: { type: String, default: '' },
	descriptionPlaceholderText: { type: String, default: '' },
	
	/* Saved item props */
	// savedId: { type: [String, Number], default: null },
	savedTitle: { type: String, default: '' },
	savedDescription: { type: String, default: '' },
});

const emit = defineEmits([
	'update:isVisible',
	'click-cancel',
	'click-update',
]);

const internalVisible = ref(props.isVisible);
const rootEl = ref(null);
const savedTitleValue = ref(props.savedTitle);
const savedDescriptionValue = ref(props.savedDescription);
const isConnectionIssued = ref(false);
const isDuplicatedEntry = ref(false);

watch(internalVisible, (newValue) => {
	emit('update:isVisible', newValue);
});

watch(() => props.isVisible, (newValue) => {
	if (!newValue) {
		isConnectionIssued.value = false;
		isDuplicatedEntry.value = false;
	}
});

const onChangeDescriptionHandler = () => {
	isConnectionIssued.value = false;
	isDuplicatedEntry.value = false;
};

const keydownEnterHandler = () => {
	if (!isConnectionIssued.value || !isDuplicatedEntry.value) {
		// console.log(';keydownEnterHandler');
		emit('click-update', {
			savedListName: savedTitleValue.value,
			savedListDescription: savedDescriptionValue.value,
		});
	}
};

const showConnectionIssueMessage = () => {
	isConnectionIssued.value = true;
};

const showDuplicatedErrorMessage = () => {
	isDuplicatedEntry.value = true;
};

defineExpose({
	showConnectionIssueMessage,
	showDuplicatedErrorMessage,
});
</script>


<template>
<template v-if="!isMobileViewport">
	<Teleport to="body">
		<Transition
			name="overlay-fade-and-zoom"
			appear
		>
			<div
				v-if="props.isVisible"
				ref="rootEl"
				class="EditUpdatePopup backdrop"
				v-bind="$attrs"
				tabindex="-1"
				@vue:unmounted="savedTitleValue = props.savedTitle; savedDescriptionValue = props.savedDescription;"
			>
				<div class="overlay-container">
					<div class="overlay-header >lg:hidden">
						<button
							type="button"
							class="absolute top-4 right-4"
							aria-label="Popup close button"
							@click="internalVisible = false"
						>
							<icon-far-xmark class="w-6 h-6 fill-primary-black-base" />
						</button>
					</div>
					<div class="overlay-body px-6">
						<h4 class="text-center">{{ props.editListTitleText }}</h4>
						<div class="mt-6">
							<TextField
								v-model="savedTitleValue"
								:placeholder="props.listNamePlaceholderText"
								@change="onChangeDescriptionHandler"
								@keydown.enter="keydownEnterHandler"
							/>
						</div>
						<div class="mt-6">
							<MHTextArea
								v-model="savedDescriptionValue"
								name="description"
								:placeholder="props.descriptionPlaceholderText"
								@change="onChangeDescriptionHandler"
							/>
						</div>
						<div v-if="(isConnectionIssued || isDuplicatedEntry)" class="error-message mt-4">
							<div v-if="isConnectionIssued" class="connection-issue text-semantic-red-base text-center">{{ props.fallbackErrorMessage }}</div>
							<div v-if="isDuplicatedEntry" class="duplicated-entry text-semantic-red-base text-center">{{ props.duplicationMessage }}</div>
						</div>
					</div>
					<div class="overlay-footer border-t border-t-neutral-grey-base">
						<CTAButton
							:ctaText="props.editCancelCtaLabel"
							sizeType="small"
							class="font-semibold"
							styleType="secondary"
							@click="emit('click-cancel')"
						/>
						<CTAButton
							:ctaText="props.editUpdateCtaLabel"
							sizeType="small"
							class="font-semibold"
							styleType="primary"
							:disabled="(isConnectionIssued || isDuplicatedEntry || !savedTitleValue || !savedDescriptionValue)"
							@click="emit('click-update', {
								savedListName: savedTitleValue,
								savedListDescription: savedDescriptionValue,
							})"
						/>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<template v-if="isMobileViewport">
	<OverlayBtmSticky
		:isVisible="props.isVisible"
		class="EditUpdateOverlay"
		@update:is-visible="internalVisible = $event;
			savedTitleValue = props.savedTitle;
			savedDescriptionValue = props.savedDescription;"
	>
		<div class="edit-update-overlay-container">
			<h4 class="text-center">{{ props.editListTitleText }}</h4>
			<div class="saved-title-container">
				<TextField
					v-model="savedTitleValue"
					:placeholder="props.listNamePlaceholderText"
					:ariaLabel="props.listNamePlaceholderText"
					@change="onChangeDescriptionHandler"
					@keydown.enter="keydownEnterHandler"
				/>
			</div>
			<div class="saved-description-container mt-3">
				<MHTextArea
					v-model="savedDescriptionValue"
					name="description"
					:placeholder="props.descriptionPlaceholderText"
					:ariaLabel="props.descriptionPlaceholderText"
					@change="onChangeDescriptionHandler"
				/>
			</div>
			<div v-if="(isConnectionIssued || isDuplicatedEntry)" class="error-message mt-4">
				<div v-if="isConnectionIssued" class="connection-issue text-semantic-red-base text-center">{{ props.fallbackErrorMessage }}</div>
				<div v-if="isDuplicatedEntry" class="duplicated-entry text-semantic-red-base text-center">{{ props.duplicationMessage }}</div>
			</div>
			<div class="overlay-footer">
				<CTAButton
					:ctaText="props.editCancelCtaLabel"
					sizeType="small"
					ctaSizing="fluid"
					class="font-semibold"
					styleType="secondary"
					containerClass="flex-grow basis-0"
					@click="emit('click-cancel')"
				/>
				<CTAButton
					:ctaText="props.editUpdateCtaLabel"
					sizeType="small"
					ctaSizing="fluid"
					class="font-semibold"
					styleType="primary"
					containerClass="flex-grow basis-0"
					:disabled="(isConnectionIssued || isDuplicatedEntry || !savedTitleValue || !savedDescriptionValue)"
					@click="emit('click-update', {
						savedListName: savedTitleValue,
						savedListDescription: savedDescriptionValue,
					})"
				/>
			</div>
		</div>
	</OverlayBtmSticky>
</template>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.EditUpdatePopup {
	.TextField,
	.TextArea {
		:deep(.inner-wrapper) {
			padding: 16px 20px;
			.input {
				--inputValueColor: var(--neutral-grey-extradark);
				font-weight: 600;
			}
			
		}
	}
	
	.TextArea {
		:deep(.inner-wrapper) {
			.input {
				--inputValueColor: var(--neutral-grey-extradark);
				font-weight: 600;
			}
			
			textarea {
				line-height: revert;
			}
		}
	}
	
}

.EditUpdateOverlay {
	// .TextField,
	// .TextArea {
	// 	:deep(.inner-wrapper) {
	// 		.input {
	// 			--inputValueColor: var(--neutral-grey-extradark);
	// 			font-weight: 600;
	// 		}
			
	// 	}
	// }
	.edit-update-overlay-container {
		padding-left: 24px;
		padding-right: 24px;
	}
	
	.saved-title-container {
		margin-top: 30px;
	}
	
	.TextArea {
		:deep(.inner-wrapper) {
			.input {
				// --inputValueColor: var(--neutral-grey-extradark);
				line-height: normal;
				height: 87px;
			}
		}
	}
}

.backdrop {
	position: fixed;
	z-index: 10000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: color.change(black, $alpha: 0.8);
	
	display: flex;
	justify-content: flex-end;
	transition: opacity 0.45s ease;
}

.overlay-container {
	background-color: var(--overlay-container-bg-color, white);
	max-width: 600px;
	width: 100%;
	margin: auto;
	overflow: hidden;
	@apply rounded-3xl;
	
	@media #{var.$query-max-md} {
		max-width: 375px;
	}
}

.overlay-header {
	position: relative;
}
.overlay-header-image {
	aspect-ratio: 3/2;
}

.overlay-body {
	padding: 44px 24px 36px 24px;
}

.overlay-footer {
	display: flex;
	align-items: center;
	justify-content: center;
	@apply gap-6;
	padding: 30px 0px 43px 0px;
	
	@media #{var.$query-max-md} {
		@apply gap-4;
	}
}

// animations
.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	opacity: 0;
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	transition: opacity 200ms;
}

.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	.overlay-container {
		transform: scale(1.2);
	}
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	.overlay-container {
		transition: transform 200ms;
	}
}
</style>